import React from "react";

import { displayInfoOf } from "./pageKinds";
import { decisionIdsOf } from "./BuilderUtils";
import { contextSummary } from "../explanation_pages/contextLayouts";

/**
 * STORYBOARD DE LA PILE — le déroulé de l'atelier, en bas du composeur.
 *
 * CE QU'IL RÉSOUT. Une page ne se juge pas seule : elle arrive après quelque
 * chose et prépare la suite. Pour passer d'une page à l'autre, il fallait
 * jusqu'ici refermer le composeur, retrouver sa ligne dans la pile de
 * l'atelier, et rouvrir — trois gestes qui font perdre le fil justement quand
 * on essaie de le suivre. Le déroulé est maintenant sous les yeux, et chaque
 * vignette est un lien.
 *
 * DEUX ÉTATS, comme le storyboard des étapes (cf. StepStoryboard) dont il
 * reprend l'habillage. Replié : une barre fine qui dit où l'on est, plus une
 * piste segmentée où chaque segment est une page. Déplié : les vignettes, avec
 * ce que chaque page contient.
 *
 * CE N'EST PLUS LUI QUI DÉCIDE DE SON REPLI. Il est devenu une LIGNE d'un
 * bandeau qui peut en compter deux (cf. ComposerStoryboard) : quand la page
 * ouverte est un module territoire, son déroulé d'étapes s'ajoute en dessous,
 * et une seule des deux lignes peut être dépliée à la fois. L'état vit donc
 * chez le bandeau, seul endroit d'où l'on peut garantir cette exclusion.
 *
 * CE QU'IL NE MONTRE PAS : les pages insérées en cours de partie par les
 * conséquences. Elles ne sont pas dans la pile, et prétendre les y placer
 * donnerait un ordre faux — une conséquence conditionnelle n'arrive pas
 * toujours. La vignette signale donc seulement qu'une page en déclenche, et le
 * graphe de l'atelier reste l'endroit où l'on suit ces branches.
 */

/** Ce que la vignette dit d'une page en une ligne. */
export function pageSummary(page) {
  if (!page) return "Page introuvable en base";
  switch (page.type) {
    case "mosaic":
    case "one-choice":
    case "command_control": {
      const count = decisionIdsOf(page).length;
      return count ? `${count} décision${count > 1 ? "s" : ""}` : "Aucune décision";
    }
    case "context":
      return contextSummary(page.content);
    case "score":
      return `Tableau de bord · ${page.content?.dashboard_type || "both"}`;
    case "two_doughnuts_graphs": {
      const count = (page.content?.list_scores || []).length;
      return `${count} score${count > 1 ? "s" : ""} en anneau`;
    }
    case "territory_intro": {
      const count = (page.content?.steps || []).length;
      return `${count} étape${count > 1 ? "s" : ""}`;
    }
    default:
      return page.type;
  }
}

export default function PileStoryboard({
  pile = [], pagesById = {}, activePageId, configName,
  expanded, onToggle, onSelect, onAdd,
}) {
  const activeIndex = pile.indexOf(activePageId);
  const inPile = activeIndex >= 0;

  const goPrevious = () => activeIndex > 0 && onSelect(pile[activeIndex - 1]);
  const goNext = () => activeIndex >= 0 && activeIndex < pile.length - 1 && onSelect(pile[activeIndex + 1]);

  const card = (pageId, idx) => {
    const page = pagesById[pageId];
    const info = displayInfoOf(page);
    const consequences = (page?.page_general_consequence || []).length;
    return (
      <button
        type="button"
        className={`step-storyboard-card${pageId === activePageId ? " is-active" : ""}`}
        onClick={() => onSelect(pageId)}
        title={pageId}
      >
        <span className="step-storyboard-index">{idx + 1}</span>
        <span className="pile-storyboard-icon" aria-hidden="true">{info?.icon || "📄"}</span>
        <span className="step-storyboard-body">
          <span className="step-storyboard-kind">{page?.title || pageId}</span>
          <span className="step-storyboard-summary">{pageSummary(page)}</span>
          <span className="step-storyboard-badges">
            <span className="step-storyboard-badge" title="Type de page">
              {page?.type || "?"}
            </span>
            {consequences > 0 && (
              <span
                className="step-storyboard-badge"
                title="Cette page ajoute des pages au parcours après elle. Voir le graphe de l'atelier pour suivre les branches"
              >
                ↳ {consequences} conséquence{consequences > 1 ? "s" : ""}
              </span>
            )}
            {!page && (
              <span className="step-storyboard-badge is-warn" title="Aucune page de ce nom en base">
                ⚠ introuvable
              </span>
            )}
          </span>
        </span>
      </button>
    );
  };

  return (
    <div className="storyboard-row pile-storyboard">
      <div className="step-storyboard-bar">
        <button
          type="button"
          className="step-storyboard-toggle"
          onClick={onToggle}
          aria-expanded={expanded}
        >
          <span className={`step-storyboard-chevron${expanded ? " is-open" : ""}`} aria-hidden="true" />
          {expanded ? "Masquer le déroulé" : "Voir le déroulé"}
        </button>

        <div className="step-storyboard-progress">
          <button
            type="button"
            className="step-storyboard-arrow"
            onClick={goPrevious}
            disabled={activeIndex <= 0}
            aria-label="Page précédente"
          >‹</button>

          <span className="step-storyboard-position">
            {inPile ? (
              <>Page <strong>{activeIndex + 1}</strong> sur {pile.length}</>
            ) : (
              /* Une page peut être éditée sans être dans la pile : ouverte
                 depuis la bibliothèque, ou n'arrivant que par une conséquence.
                 Le dire vaut mieux que de la placer arbitrairement quelque part
                 dans le déroulé. */
              <>Hors du déroulé <span className="step-storyboard-position-abs">({pile.length} page{pile.length > 1 ? "s" : ""} dans la pile)</span></>
            )}
            {configName && <span className="step-storyboard-position-abs"> · {configName}</span>}
          </span>

          {/* Piste segmentée : l'avancement ET un accès direct. Une barre de
              progression dirait où l'on en est sans permettre d'y aller ; un
              segment par page fait les deux. */}
          <div className="step-storyboard-track" role="tablist" aria-label="Pages de l'atelier">
            <span className="step-storyboard-trackgroup" style={{ flexGrow: pile.length || 1 }}>
              {pile.map((pageId, idx) => (
                <button
                  key={`${pageId}-${idx}`}
                  type="button"
                  role="tab"
                  aria-selected={pageId === activePageId}
                  aria-label={`Page ${idx + 1} : ${pagesById[pageId]?.title || pageId}`}
                  title={`${idx + 1}. ${pagesById[pageId]?.title || pageId}`}
                  className={`step-storyboard-seg${pageId === activePageId ? " is-active" : ""}`}
                  onClick={() => onSelect(pageId)}
                />
              ))}
            </span>
          </div>

          <button
            type="button"
            className="step-storyboard-arrow"
            onClick={goNext}
            disabled={activeIndex < 0 || activeIndex >= pile.length - 1}
            aria-label="Page suivante"
          >›</button>
        </div>

        {onAdd && (
          <button type="button" className="step-storyboard-addbtn" onClick={onAdd}>
            + Page
          </button>
        )}
      </div>

      {expanded && (
        <div className="step-storyboard-strip">
          {pile.map((pageId, idx) => (
            <React.Fragment key={`${pageId}-${idx}`}>
              {idx > 0 && <span className="step-storyboard-link" aria-hidden="true" />}
              {card(pageId, idx)}
            </React.Fragment>
          ))}
          {pile.length === 0 && (
            <span className="step-storyboard-summary" style={{ padding: "0 10px" }}>
              La pile de cet atelier est vide.
            </span>
          )}
        </div>
      )}
    </div>
  );
}
